<template>
  <div>
    <main-layout
      controlsBg="light"
      :hasControlsBar="false"
      :breadcrumb="getBreadcrumb">
      <div class="container">
        <grid-row class="margin-top-xl" column-align="center">
          <grid-col col-size="6">
            <div id="header">
              <h1 class="header-title">
                <chevron @click="$router.push('/workspace')"/>
                {{ $t('health.prescriberPreferences.title') }}
              </h1>
            </div>
          </grid-col>
        </grid-row>

        <grid-row class="margin-top-xl" column-align="center">
          <grid-col col-size="6">
            <div class="card">
              <h2 class="card__title">{{ $t('health.prescriberPreferences.catalogAccess.title') }}</h2>
              <p class="card__description">
                {{ $t('health.prescriberPreferences.catalogAccess.description') }}
              </p>

              <a class="learn-more" @click="showExplanation = true">
                {{ $t('health.prescriberPreferences.catalogAccess.learnMore') }}
              </a>

              <div class="options margin-top-l">
                <label v-for="option in options" :key="option.value" class="option">
                  <input
                    type="radio"
                    name="catalogAccess"
                    :value="option.value"
                    v-model="selectedAccess"
                  />
                  <span class="option__label">{{ option.label }}</span>
                </label>
              </div>

              <div class="actions margin-top-l">
                <button
                  class="btn btn--secondary"
                  @click="reload"
                  :disabled="!isDirty || isSaving">
                  {{ $t('commons.cancel') }}
                </button>
                <button
                  class="btn btn--primary"
                  @click="save"
                  :disabled="!isDirty || isSaving">
                  {{ isSaving ? $t('commons.saving') : $t('commons.save') }}
                </button>
              </div>
            </div>
          </grid-col>
        </grid-row>
      </div>

      <modal v-if="showExplanation" @close="showExplanation = false">
        <h3 slot="header">{{ $t('health.prescriberPreferences.catalogAccess.explanationTitle') }}</h3>
        <div slot="body" class="explanation">
          <p>{{ $t('health.prescriberPreferences.catalogAccess.explanationIntro') }}</p>
          <ul>
            <li><strong>{{ $t('health.prescriberPreferences.catalogAccess.levels.none.label') }}:</strong>
              {{ $t('health.prescriberPreferences.catalogAccess.levels.none.description') }}</li>
            <li><strong>{{ $t('health.prescriberPreferences.catalogAccess.levels.substituteOne.label') }}:</strong>
              {{ $t('health.prescriberPreferences.catalogAccess.levels.substituteOne.description') }}</li>
            <li><strong>{{ $t('health.prescriberPreferences.catalogAccess.levels.regenerateDay.label') }}:</strong>
              {{ $t('health.prescriberPreferences.catalogAccess.levels.regenerateDay.description') }}</li>
            <li><strong>{{ $t('health.prescriberPreferences.catalogAccess.levels.full.label') }}:</strong>
              {{ $t('health.prescriberPreferences.catalogAccess.levels.full.description') }}</li>
          </ul>
          <p class="warning">⚠️ {{ $t('health.prescriberPreferences.catalogAccess.explanationWarning') }}</p>
        </div>
        <div slot="footer" class="modal-single-action">
          <btn-pill btn-size="s" @click="showExplanation = false">
            {{ $t('commons.close') }}
          </btn-pill>
        </div>
      </modal>
    </main-layout>
  </div>
</template>

<script>
import MainLayout from '@/layouts/MainLayout';
import GridRow from '@/components/shared/grid/GridRow';
import GridCol from '@/components/shared/grid/GridCol';
import Chevron from '@/components/shared/Chevron';
import Modal from '@/components/shared/dialogs/Modal';
import BtnPill from '@/components/shared/buttons/BtnPill';
import prescriberPreferencesApi from '../../api/prescriberPreferences';

export default {
  name: 'PrescriberPreferences',
  components: {
    MainLayout,
    GridRow,
    GridCol,
    Chevron,
    Modal,
    BtnPill
  },
  data () {
    return {
      breadcrumb: [
        { link: '/workspace', name: this.$t('menu.workspace') },
        { link: '/preferences', name: this.$t('health.prescriberPreferences.title'), disabled: true }
      ],
      initialAccess: 'none',
      selectedAccess: 'none',
      isSaving: false,
      showExplanation: false
    };
  },
  computed: {
    getBreadcrumb () {
      return this.breadcrumb;
    },
    isDirty () {
      return this.selectedAccess !== this.initialAccess;
    },
    options () {
      return [
        { value: 'none', label: this.$t('health.prescriberPreferences.catalogAccess.levels.none.label') },
        { value: 'substitute_one', label: this.$t('health.prescriberPreferences.catalogAccess.levels.substituteOne.label') },
        { value: 'regenerate_day', label: this.$t('health.prescriberPreferences.catalogAccess.levels.regenerateDay.label') },
        { value: 'full', label: this.$t('health.prescriberPreferences.catalogAccess.levels.full.label') }
      ];
    }
  },
  mounted () {
    this.reload();
  },
  methods: {
    async reload () {
      try {
        const result = await prescriberPreferencesApi.getCatalogAccess(this);
        this.initialAccess = result.glucovibesCatalogAccess || 'none';
        this.selectedAccess = this.initialAccess;
      } catch (error) {
        console.error(error);
      }
    },
    async save () {
      this.isSaving = true;
      try {
        await prescriberPreferencesApi.setCatalogAccess(this, this.selectedAccess);
        this.initialAccess = this.selectedAccess;
        alert(this.$t('health.prescriberPreferences.saved'));
      } catch (error) {
        console.error(error);
        alert(this.$t('health.prescriberPreferences.saveError'));
      } finally {
        this.isSaving = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "src/stylesheets/variables";

.card {
  background: white;
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

  &__title {
    margin-top: 0;
    margin-bottom: 0.5rem;
  }

  &__description {
    color: $black-light4;
    margin-bottom: 1rem;
    line-height: 1.5;
  }
}

.learn-more {
  color: $secondary;
  cursor: pointer;
  text-decoration: underline;
  font-size: 0.9rem;
}

.options {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.option {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  padding: 0.75rem;
  border: 1px solid $black-light9;
  border-radius: 6px;
  transition: background 0.15s;

  &:hover {
    background: $disabled;
  }

  input[type="radio"] {
    margin: 0;
  }

  &__label {
    font-size: 1rem;
  }
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}

.btn {
  padding: 0.6rem 1.2rem;
  border-radius: 6px;
  font-size: 0.95rem;
  cursor: pointer;
  border: none;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &--primary {
    background: $secondary;
    color: white;
  }

  &--secondary {
    background: transparent;
    border: 1px solid $black-light4;
    color: $black-light4;
  }
}

.explanation {
  ul {
    padding-left: 1.5rem;
    line-height: 1.6;
  }
  li {
    margin-bottom: 0.5rem;
  }
  .warning {
    margin-top: 1rem;
    padding: 0.75rem;
    background: #FFF3CD;
    border-left: 3px solid #FFC107;
    border-radius: 4px;
  }
}

.modal-single-action {
  display: flex;
  justify-content: flex-end;
}
</style>



// WEBPACK FOOTER //
// src/brand/health/layouts/nutritionalPlan/PrescriberPreferences.vue